import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, MessageCircle, ChevronRight, MapPin, Search, Compass, Map, ShoppingBag, Lightbulb, AlertTriangle, Palmtree, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { playConnectionSound } from "@/components/GiftSystem";
import { AdeRefreshButton } from "@/components/AdeRefreshButton";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";
import { ProBadge } from "@/components/ProBadge";
import { playSound } from "@/hooks/useSounds";
import { IslandSearch } from "@/components/IslandSearch";
import { IslandTravelAnimation } from "@/components/IslandTravelAnimation";
import { IslandView } from "@/components/IslandView";
import { IslandShop } from "@/components/IslandShop";
import { IslandScene } from "@/components/IslandScene";
import { AdeWorldMap } from "@/components/AdeWorldMap";
import { useLang } from "@/i18n";
import "leaflet/dist/leaflet.css";

// Here Page - Tabs: Vicini (radar with map) | Richieste (person+) | Collegati (chain)

// Tab Icons
const RadarIcon = ({ active, className = "" }) => (
  <svg viewBox="0 0 32 32" className={`w-7 h-7 ${className}`}>
    <defs>
      <linearGradient id="radarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F97316" />
        <stop offset="50%" stopColor="#EC4899" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="14" fill="none" stroke={active ? "url(#radarGrad)" : "#9CA3AF"} strokeWidth="1.5" strokeDasharray="4 2"/>
    <circle cx="16" cy="16" r="9" fill="none" stroke={active ? "url(#radarGrad)" : "#9CA3AF"} strokeWidth="1.5" strokeDasharray="3 2"/>
    <circle cx="16" cy="16" r="4" fill={active ? "url(#radarGrad)" : "#9CA3AF"}/>
    <ellipse cx="11" cy="11" rx="2.5" ry="2.5" fill={active ? "url(#radarGrad)" : "#9CA3AF"}/>
    <path d="M7 17 Q11 14 15 17" fill="none" stroke={active ? "url(#radarGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
    <ellipse cx="21" cy="11" rx="2.5" ry="2.5" fill={active ? "url(#radarGrad)" : "#9CA3AF"}/>
    <path d="M17 17 Q21 14 25 17" fill="none" stroke={active ? "url(#radarGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IslandIcon = ({ active, className = "" }) => (
  <svg viewBox="0 0 32 32" className={`w-7 h-7 ${className}`}>
    <defs>
      <linearGradient id="islandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06B6D4" />
        <stop offset="50%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
    {/* Island base/sand */}
    <ellipse cx="16" cy="24" rx="12" ry="4" fill={active ? "url(#islandGrad)" : "#9CA3AF"}/>
    {/* Palm tree trunk */}
    <path d="M16 24 Q17 18 15 12" fill="none" stroke={active ? "url(#islandGrad)" : "#9CA3AF"} strokeWidth="2.5" strokeLinecap="round"/>
    {/* Palm leaves */}
    <path d="M15 12 Q8 10 6 14" fill="none" stroke={active ? "url(#islandGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
    <path d="M15 12 Q12 6 8 6" fill="none" stroke={active ? "url(#islandGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
    <path d="M15 12 Q18 6 22 7" fill="none" stroke={active ? "url(#islandGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
    <path d="M15 12 Q22 10 24 14" fill="none" stroke={active ? "url(#islandGrad)" : "#9CA3AF"} strokeWidth="2" strokeLinecap="round"/>
    {/* Small house */}
    <rect x="19" y="19" width="6" height="5" rx="1" fill={active ? "url(#islandGrad)" : "#9CA3AF"}/>
    <path d="M18 19 L22 15 L26 19" fill={active ? "url(#islandGrad)" : "#9CA3AF"}/>
  </svg>
);

const PersonPlusIcon = ({ active, className = "" }) => (
  <svg viewBox="0 0 32 32" className={`w-7 h-7 ${className}`}>
    <defs>
      <linearGradient id="personPlusGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F97316" />
        <stop offset="50%" stopColor="#EC4899" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
    <circle cx="13" cy="10" r="5" fill={active ? "url(#personPlusGrad)" : "#9CA3AF"}/>
    <path d="M4 26 C4 20 8 16 13 16 C18 16 22 20 22 26" fill={active ? "url(#personPlusGrad)" : "#9CA3AF"}/>
    <line x1="26" y1="8" x2="26" y2="18" stroke={active ? "url(#personPlusGrad)" : "#9CA3AF"} strokeWidth="3" strokeLinecap="round"/>
    <line x1="21" y1="13" x2="31" y2="13" stroke={active ? "url(#personPlusGrad)" : "#9CA3AF"} strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const ChainLinkIcon = ({ active, className = "" }) => (
  <svg viewBox="0 0 32 32" className={`w-7 h-7 ${className}`}>
    <defs>
      <linearGradient id="chainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F97316" />
        <stop offset="50%" stopColor="#EC4899" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
    <rect x="2" y="10" width="12" height="12" rx="6" fill="none" stroke={active ? "url(#chainGrad)" : "#9CA3AF"} strokeWidth="3"/>
    <rect x="18" y="10" width="12" height="12" rx="6" fill="none" stroke={active ? "url(#chainGrad)" : "#9CA3AF"} strokeWidth="3"/>
    <rect x="12" y="14" width="8" height="4" rx="2" fill={active ? "url(#chainGrad)" : "#9CA3AF"}/>
  </svg>
);

// Connection Status Bar - supports pending/accepted/rejected states
const ConnectionStatusBar = ({ status = 'pending' }) => {
  const getConfig = () => {
    switch (status) {
      case 'accepted':
        return {
          bgColor: 'bg-green-200',
          barColor: 'from-transparent via-green-500 to-transparent',
          glowColor: 'from-green-400/30 via-emerald-400/30 to-green-400/30',
          label: 'Accettato!',
          labelColor: 'text-green-600',
          icon: <Check className="w-3 h-3 text-white" strokeWidth={3} />,
          iconBg: 'from-green-400 to-emerald-500',
          iconShadow: 'shadow-green-400/50',
          animate: false
        };
      case 'declined':
      case 'rejected':
        return {
          bgColor: 'bg-red-200',
          barColor: 'from-transparent via-red-500 to-transparent',
          glowColor: 'from-red-400/30 via-pink-400/30 to-red-400/30',
          label: 'Rifiutato',
          labelColor: 'text-red-500',
          icon: <X className="w-3 h-3 text-white" strokeWidth={3} />,
          iconBg: 'from-red-400 to-rose-500',
          iconShadow: 'shadow-red-400/50',
          animate: false
        };
      default: // pending
        return {
          bgColor: 'bg-gray-200',
          barColor: 'from-transparent via-orange-400 to-transparent',
          glowColor: 'from-orange-400/20 via-pink-400/20 to-purple-400/20',
          label: 'In attesa',
          labelColor: 'text-orange-500',
          icon: null,
          iconBg: '',
          iconShadow: '',
          animate: true
        };
    }
  };
  
  const config = getConfig();
  
  return (
    <div className="flex items-center gap-2">
      <div className={`relative w-20 h-2 rounded-full overflow-hidden ${config.bgColor} transition-all duration-500`}>
        {config.animate ? (
          <>
            <div className={`absolute inset-y-0 w-8 bg-gradient-to-r ${config.barColor} animate-scan-bar rounded-full`} />
            <div className={`absolute inset-0 bg-gradient-to-r ${config.glowColor} animate-pulse`} />
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-r ${config.barColor.replace('via-', '')} animate-pulse`} 
               style={{ background: status === 'accepted' ? 'linear-gradient(90deg, #22c55e, #10b981)' : 'linear-gradient(90deg, #ef4444, #f43f5e)' }} />
        )}
      </div>
      {config.icon && (
        <div className={`w-5 h-5 rounded-full bg-gradient-to-r ${config.iconBg} flex items-center justify-center shadow-lg ${config.iconShadow} animate-bounce`}>
          {config.icon}
        </div>
      )}
      <span className={`text-[10px] font-medium ${config.labelColor}`}>{config.label}</span>
    </div>
  );
};

// Legacy component for backward compatibility
const PendingConnectionBar = () => <ConnectionStatusBar status="pending" />;

// Connected status indicator (green pulse line)
const ConnectedIndicator = () => (
  <div className="flex items-center gap-2">
    <div className="relative w-16 h-1.5 rounded-full overflow-hidden bg-green-200">
      <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer" />
    </div>
    <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-400/30">
      <Check className="w-3 h-3 text-white" strokeWidth={3} />
    </div>
  </div>
);

// === EXPLORE TAB (inline, a livello di modulo per non essere rimontato) ===
const ExploreInline = () => {
const navigate = useNavigate();
const { t } = useLang();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [peopleResults, setPeopleResults] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loadingExplore, setLoadingExplore] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const res = await api.get("/explore/trending");
      setTrending((res.data.trending?.length ? res.data.trending : res.data.popular) || []);
    } catch (e) { console.error(e); }
    finally { setLoadingExplore(false); }
  };

  const handleSearch = async (q) => {
    setQuery(q);
    const clean = q.trim().replace(/^[*@#]/, "");
    if (clean.length < 1) { setSearchResults([]); setPeopleResults([]); return; }
    const onlyTags = q.trim().startsWith("#") || q.trim().startsWith("*");
    const onlyPeople = q.trim().startsWith("@");
    const [tags, people] = await Promise.all([
      onlyPeople ? Promise.resolve(null) : api.get(`/explore/search?q=${encodeURIComponent(clean)}`).catch(() => null),
      onlyTags || clean.length < 2 ? Promise.resolve(null) : api.get(`/explore/people?q=${encodeURIComponent(clean)}`).catch(() => null),
    ]);
    setSearchResults(tags?.data?.results || []);
    setPeopleResults(people?.data?.results || []);
  };

  return (
    <div data-testid="explore-tab">
      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Cerca #AdETag, persone, @username..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white border-0 focus:ring-2 focus:ring-amber-400 outline-none"
          data-testid="explore-search-input"
        />
      </div>

      {/* Search results */}
      {query.length > 0 && searchResults.length > 0 && (
        <div className="mb-4 space-y-1">
          {searchResults.map((r) => (
            <button
              key={r.tag}
              onClick={() => navigate(`/app/explore/${r.tag}`)}
              className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              data-testid={`search-result-${r.tag}`}
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <span className="text-amber-600 font-bold">*</span>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">#{r.tag}</p>
                <p className="text-xs text-gray-400">{r.count} post</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Persone: collegamenti + ADE Pro */}
      {query.length > 0 && peopleResults.length > 0 && (
        <div className="mb-4 space-y-1" data-testid="explore-people-list">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 px-1 mb-1">Persone</p>
          {peopleResults.map((u) => (
            <button
              key={u.user_id}
              onClick={() => navigate(`/app/user/${u.user_id}`)}
              className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              data-testid={`people-result-${u.user_id}`}
            >
              <GoldenRingAvatar user={u} size="sm" />
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{u.name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {u.username ? `@${u.username}` : u.profession || "Profilo ADE"}
                </p>
              </div>
              {u.is_ade_pro && (
                <span className="text-[8.5px] font-black uppercase tracking-wider text-amber-600 border border-amber-300 bg-amber-50 rounded-full px-2 py-0.5">Pro</span>
              )}
            </button>
          ))}
        </div>
      )}

      {query.length > 0 && searchResults.length === 0 && peopleResults.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-6">Nessun risultato</p>
      )}

      {/* Trending */}
      {query.length === 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-bold text-gray-900 dark:text-white">AdETag Popolari</span>
          </div>
          {loadingExplore ? (
            <div className="flex justify-center py-8"><AdeLoadingSpinner size="md" /></div>
          ) : trending.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t("noTagYet")}</p>
              <p className="text-xs mt-1">Usa #tag nei tuoi post per iniziare!</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {trending.map((t, i) => (
                <button
                  key={t.tag}
                  onClick={() => navigate(`/app/explore/${t.tag}`)}
                  className={`px-4 py-2 rounded-full border transition-colors ${
                    i < 3
                      ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400"
                      : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                  }`}
                  data-testid={`trending-${t.tag}`}
                >
                  <span className="text-sm font-medium">#{t.tag}</span>
                  <span className="text-xs text-gray-400 ml-1.5">{t.count}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function Here() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('nearby');
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const myMarkerRef = useRef(null);
  const headingRef = useRef(null);
  const prevLocRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [previousNearbyCount, setPreviousNearbyCount] = useState(0);
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [requestHistory, setRequestHistory] = useState([]);
  // tick per far svanire progressivamente le richieste già gestite (24h)
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowTs(Date.now()), 30000);
    return () => clearInterval(iv);
  }, []);
  const [myLocation, setMyLocation] = useState(null);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [isOnAdeIsland, setIsOnAdeIsland] = useState(false);
  
  // Island exploration states
  const [showIslandSearch, setShowIslandSearch] = useState(false);
  const [showIslandShop, setShowIslandShop] = useState(false);
  const [selectedIsland, setSelectedIsland] = useState(null);
  const [showTravelAnimation, setShowTravelAnimation] = useState(false);
  const [myIslandName, setMyIslandName] = useState(null);
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  const highlightedCardRef = useRef(null);

  // Auto-open Island Shop when ?shop=1
  useEffect(() => {
    if (searchParams.get('shop') === '1') {
      setShowIslandShop(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Deep-link: ?tab=connections[&highlight=<request_id>] from notification click
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['nearby', 'island', 'connections', 'explore'];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
    const hl = searchParams.get('highlight');
    if (hl) {
      setHighlightedRequestId(hl);
      // Auto-clear highlight after 8s so it doesn't stick between navigations
      const timer = setTimeout(() => setHighlightedRequestId(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  // Scroll highlighted request card into view when pendingRequests are loaded
  useEffect(() => {
    if (highlightedRequestId && highlightedCardRef.current) {
      highlightedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedRequestId, pendingRequests]);
  
  // Real GPS geolocation
  const updateLocationToBackend = useCallback(async (coords) => {
    try {
      await api.post('/location', {
        latitude: coords.latitude,
        longitude: coords.longitude
      });
      setMyLocation(coords);
      setGpsEnabled(true);
      setGpsError(null);
    } catch (err) {
      console.error('Failed to update location:', err);
    }
  }, []);
  
  // Initialize real GPS
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          updateLocationToBackend(coords);
        },
        (err) => {
          console.log('GPS error, using backend location:', err.message);
          setGpsError(err.message);
          setGpsEnabled(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
      
      // Watch position for continuous updates
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          // Heading: prefer GPS heading, otherwise bearing from previous point
          const gpsH = position.coords.heading;
          if (gpsH !== null && gpsH !== undefined && !isNaN(gpsH)) {
            headingRef.current = gpsH;
          } else if (prevLocRef.current) {
            const toRad = (d) => (d * Math.PI) / 180;
            const { latitude: la1, longitude: lo1 } = prevLocRef.current;
            const dist = Math.hypot(coords.latitude - la1, coords.longitude - lo1) * 111000;
            if (dist > 2) {
              const y = Math.sin(toRad(coords.longitude - lo1)) * Math.cos(toRad(coords.latitude));
              const x = Math.cos(toRad(la1)) * Math.sin(toRad(coords.latitude)) -
                        Math.sin(toRad(la1)) * Math.cos(toRad(coords.latitude)) * Math.cos(toRad(coords.longitude - lo1));
              headingRef.current = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
            }
          }
          prevLocRef.current = coords;
          updateLocationToBackend(coords);
        },
        () => {}, // Ignore watch errors
        { enableHighAccuracy: true, maximumAge: 10000 }
      );
      
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, [updateLocationToBackend]);
  
  const fetchData = useCallback(async (showToast = false) => {
    try {
      const [nearbyRes, connectionsRes, requestsRes, sentRes, historyRes, myIslandRes] = await Promise.all([
        api.get('/nearby'),
        api.get('/connections'),
        api.get('/connections/requests'),
        api.get('/connections/sent'),
        api.get('/connections/history').catch(() => ({ data: { requests: [] } })),
        api.get('/islands/my/island').catch(() => ({ data: { island_name: null } }))
      ]);
      
      const newNearbyUsers = nearbyRes.data.nearby_users || [];
      
      // Check if user is on ADE Island (no GPS)
      setIsOnAdeIsland(nearbyRes.data.is_ade_island || false);
      
      // Set my island name
      setMyIslandName(myIslandRes.data.island_name);
      
      // Show toast if new users found during refresh
      if (showToast && previousNearbyCount > 0) {
        const newCount = newNearbyUsers.length - previousNearbyCount;
        if (newCount > 0) {
          if (nearbyRes.data.is_ade_island) {
            toast.success(`${newCount} ${newCount === 1 ? 'nuovo naufrago' : 'nuovi naufraghi'} su ADE Island!`);
          } else {
            toast.success(`${newCount} ${newCount === 1 ? 'nuovo utente' : 'nuovi utenti'} nelle vicinanze!`);
          }
          playSound('request');
        } else if (newCount < 0) {
          toast.info(`${Math.abs(newCount)} ${Math.abs(newCount) === 1 ? 'utente si è' : 'utenti si sono'} allontanati`);
        }
      }
      
      setPreviousNearbyCount(newNearbyUsers.length);
      setNearbyUsers(newNearbyUsers);
      setConnections(connectionsRes.data.connections || []);
      setPendingRequests(requestsRes.data.requests || []);
      setSentRequests(sentRes.data.requests || []);
      setRequestHistory(historyRes.data.requests || []);
      
      // Update location from backend
      const loc = nearbyRes.data.my_location;
      if (loc) {
        setMyLocation(loc);
        setGpsEnabled(loc.has_real_gps || false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (showToast) {
        toast.error("Errore nell'aggiornamento");
      }
    } finally {
      setLoading(false);
    }
  }, [previousNearbyCount]);
  
  // Refresh sound
  const playRefreshSound = () => {
    try {
      const sound = new Audio('/sounds/refresh.wav');
      sound.volume = 0.3;
      sound.play().catch(() => {});
    } catch (e) {}
  };
  
  // Manual refresh handler for ADE logo button
  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    
    // Play refresh sound
    playRefreshSound();
    
    await fetchData(true); // Check for new users
    // Keep spinning for 3 seconds minimum
    setTimeout(() => setIsRefreshing(false), 3000);
  }, [fetchData, isRefreshing]);
  
  useEffect(() => {
    fetchData(false); // No toast on initial load
  }, []);
  
  // Auto-refresh for nearby users every 10 seconds when on nearby tab
  useEffect(() => {
    if (activeTab !== 'nearby') return;
    
    const autoRefreshInterval = setInterval(() => {
      fetchData(true); // Show toast on auto-refresh too
    }, 10000); // 10 seconds
    
    return () => clearInterval(autoRefreshInterval);
  }, [activeTab, fetchData]);
  
  // Polling for connection updates (every 5 seconds when on requests tab)
  useEffect(() => {
    if (activeTab !== 'connections') return;
    
    // Track previously seen accepted requests to detect new ones
    const seenAcceptedIds = new Set(
      requestHistory.filter(r => r.status === 'accepted').map(r => r.request_id)
    );
    
    const pollInterval = setInterval(async () => {
      try {
        const [sentRes, historyRes] = await Promise.all([
          api.get('/connections/sent'),
          api.get('/connections/history').catch(() => ({ data: { requests: [] } }))
        ]);
        
        const newHistory = historyRes.data.requests || [];
        
        // Check for newly accepted requests
        const newAccepted = newHistory.filter(
          r => r.status === 'accepted' && r.direction === 'sent' && !seenAcceptedIds.has(r.request_id)
        );
        
        if (newAccepted.length > 0) {
          // Play success sound for new acceptance!
          playConnectionSound('accept');
          toast.success(`${newAccepted[0].from_user?.name || 'Qualcuno'} ha accettato la tua richiesta!`);
          // Add to seen set
          newAccepted.forEach(r => seenAcceptedIds.add(r.request_id));
        }
        
        setSentRequests(sentRes.data.requests || []);
        setRequestHistory(newHistory);
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [activeTab, requestHistory]);
  
  // Load Leaflet map
  useEffect(() => {
    if (activeTab !== 'nearby' || !myLocation || mapLoaded) return;
    
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.body.appendChild(script);
    } else {
      initMap();
    }
  }, [activeTab, myLocation, mapLoaded]);

  // Live follow: keep map and "Tu" marker in sync with GPS updates
  useEffect(() => {
    if (!mapLoaded || !myLocation || !mapInstanceRef.current) return;
    const ll = [myLocation.latitude, myLocation.longitude];
    try {
      myMarkerRef.current?.setLatLng(ll);
      mapInstanceRef.current.panTo(ll, { animate: true, duration: 0.8 });
      // rotate heading tick towards walking direction
      const cone = myMarkerRef.current?.getElement?.()?.querySelector('.tu-heading-wrap');
      if (cone && headingRef.current !== null) {
        cone.style.transform = `rotate(${headingRef.current}deg)`;
      }
    } catch {}
  }, [myLocation, mapLoaded]);
  
  const initMap = () => {
    if (!mapRef.current || !window.L || !myLocation) return;
    
    if (mapRef.current._leaflet_id) {
      mapRef.current._leaflet_id = null;
      mapRef.current.innerHTML = '';
    }
    
    const map = window.L.map(mapRef.current).setView([myLocation.latitude, myLocation.longitude], 16);
    
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    
    // My location marker (with heading beam)
    const myIcon = window.L.divIcon({
      className: 'my-location-marker',
      html: `
        <div class="relative" style="width:32px;height:32px;">
          <div class="tu-heading-wrap" style="position:absolute; inset:-9px; transform: rotate(0deg); transition: transform 0.6s ease; pointer-events:none;">
            <div style="position:absolute; left:50%; top:0; transform:translateX(-50%); width:3px; height:8px; border-radius:2px; background:#ec4899; box-shadow: 0 0 5px rgba(236,72,153,0.9);"></div>
          </div>
          <div class="absolute inset-0 w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 animate-ping opacity-30"></div>
          <div class="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 flex items-center justify-center shadow-lg">
            <span class="text-white text-xs font-bold">Tu</span>
          </div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
    
    myMarkerRef.current = window.L.marker([myLocation.latitude, myLocation.longitude], { icon: myIcon }).addTo(map);
    mapInstanceRef.current = map;
    
    nearbyUsers.forEach(user => {
      if (user.latitude && user.longitude) {
        // Determine border color based on Golden Ring status
        const hasGoldenRing = user.golden?.level > 0;
        const borderStyle = hasGoldenRing 
          ? 'border: 3px solid #EAB308; box-shadow: 0 0 10px rgba(234, 179, 8, 0.7);' 
          : 'border: 2px solid #F472B6;';
        const textColor = hasGoldenRing ? 'color: #CA8A04;' : 'color: #EC4899;';
        
        const userIcon = window.L.divIcon({
          className: 'nearby-user-marker',
          html: `
            <div style="width: 40px; height: 40px; border-radius: 50%; background: white; ${borderStyle} display: flex; align-items: center; justify-content: center; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
              ${user.picture 
                ? `<img src="${user.picture}" style="width: 100%; height: 100%; object-fit: cover;"/>` 
                : `<span style="${textColor} font-weight: bold; font-size: 14px;">${user.name?.charAt(0)?.toUpperCase()}</span>`
              }
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });
        
        window.L.marker([user.latitude, user.longitude], { icon: userIcon })
          .addTo(map)
          .bindPopup(`<b>${user.name}</b>${hasGoldenRing ? ' <span style="color: gold;">*</span>' : ''}<br>${user.distance}m`);
      }
    });
    
    setMapLoaded(true);
  };
  
  const handleAccept = async (requestId) => {
    try {
      await api.post(`/connections/accept/${requestId}`);
      playConnectionSound('accept');
      toast.success("Collegamento accettato!");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'accettazione");
    }
  };
  
  const handleDecline = async (requestId) => {
    try {
      await api.post(`/connections/decline/${requestId}`);
      playConnectionSound('cancel');
      toast.success("Richiesta rifiutata");
      fetchData();
    } catch (error) {
      toast.error("Errore nel rifiuto");
    }
  };
  
  const handleCancelRequest = async (requestId) => {
    try {
      await api.delete(`/connections/cancel/${requestId}`);
      playConnectionSound('cancel');
      toast.success("Richiesta annullata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'annullamento");
    }
  };
  
  const handleConnect = async (userId) => {
    try {
      playConnectionSound('send');
      const response = await api.post(`/connections/request/${userId}`);
      if (response.data.status === 'connected') {
        playConnectionSound('accept');
        toast.success("Collegamento stabilito!");
      } else {
        toast.success("Richiesta inviata!");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    }
  };
  
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };
  
  const hasGoldenRing = (user) => user?.golden?.level > 0 || user?.is_verified;
  
  // Check if we have a pending request to this user
  const getPendingRequestId = (userId) => {
    const request = sentRequests.find(r => r.to_user?.user_id === userId);
    return request?.request_id || null;
  };
  
  // ADE Island special view with real island image
  const renderAdeIsland = () => (
    <div className="space-y-4">
      {/* ADE Island Header with real image */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl">
        {/* Island background image */}
        <div className="relative">
          <img 
            src="/images/ade-island.png" 
            alt="ADE Island" 
            className="w-full h-56 object-cover"
          />
          {/* Overlay gradient for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Island content overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h2 className="text-2xl font-bold drop-shadow-lg">ADE Island</h2>
            <p className="text-cyan-100 text-sm drop-shadow">
              L'isola dei naufraghi senza GPS
            </p>
            {myIslandName && (
              <p className="text-xs text-cyan-200 mt-1">
                La tua isola: <span className="font-bold text-white">{myIslandName}</span>
              </p>
            )}
          </div>
        </div>
        
        {/* Search Islands Button - NEW */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4">
          <button
            onClick={() => setShowIslandSearch(true)}
            className="w-full py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2"
            data-testid="search-islands-btn"
          >
            <Compass className="w-5 h-5" />
            Cerca un'Isola da Visitare
          </button>
          <p className="text-xs text-cyan-100 text-center mt-2">
            Ogni utente ha la sua isola unica. Cercala per nome!
          </p>
        </div>
        
        {/* GPS activation section */}
        <div className="bg-gradient-to-b from-blue-900 to-blue-950 p-4 text-white">
          <p className="text-sm text-center mb-3 text-cyan-100">
            Sei su ADE Island perché il GPS non è attivo.<br/>
            <strong>Attivalo</strong> per trovare persone realmente vicine a te!
          </p>
          <button
            onClick={() => {
              if ('geolocation' in navigator) {
                toast.info('Richiesta permesso GPS...');
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    updateLocationToBackend({
                      latitude: pos.coords.latitude,
                      longitude: pos.coords.longitude
                    });
                    toast.success('GPS attivato: stai lasciando ADE Island');
                    fetchData(true);
                  },
                  (err) => {
                    console.error('GPS Error:', err);
                    if (err.code === err.PERMISSION_DENIED) {
                      toast.error('Permesso negato. Vai in Impostazioni > Privacy > Posizione e abilita per questo sito.');
                    } else if (err.code === err.POSITION_UNAVAILABLE) {
                      toast.error('Posizione non disponibile. Assicurati che il GPS sia attivo sul dispositivo.');
                    } else if (err.code === err.TIMEOUT) {
                      toast.error('Timeout. Riprova in un luogo con migliore segnale GPS.');
                    } else {
                      toast.error('Errore GPS. Riprova.');
                    }
                  },
                  { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
                );
              } else {
                toast.error('GPS non supportato su questo browser');
              }
            }}
            className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            {t('activateGps')} e Lascia l'Isola
          </button>
        </div>
      </div>
      
      {/* Other castaways on the island */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 px-1">
          {nearbyUsers.length} {nearbyUsers.length === 1 ? 'naufrago' : 'naufraghi'} su ADE Island
        </h3>
        
        {nearbyUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100">
            <img src="/images/ade-island.png" alt="Island" className="w-20 h-20 mx-auto mb-2 rounded-full object-cover opacity-50" />
            <p className="text-sm font-medium">{t("aloneOnIsland")}</p>
            <p className="text-xs mt-1">{t("enableGpsHint")}</p>
          </div>
        ) : (
          nearbyUsers.map((user) => {
            const isConnected = connections.some(c => c.user?.user_id === user.user_id);
            const pendingRequestId = getPendingRequestId(user.user_id);
            
            return (
              <div 
                key={user.user_id}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl shadow-sm border border-blue-100"
              >
                <GoldenRingAvatar 
                  user={user}
                  size="md"
                  onClick={() => navigate(`/app/user/${user.user_id}`)}
                  className="cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-blue-600">{t("castaway")}</p>
                </div>
                <ConnectionStatusBar
                  isConnected={isConnected}
                  status={user.has_pending_request ? (user.request_direction === 'sent' ? 'pending' : 'received') : null}
                  onConnect={() => handleConnect(user.user_id)}
                  onCancel={pendingRequestId ? () => handleCancelRequest(pendingRequestId) : null}
                  onAccept={user.request_direction === 'received' ? () => {
                    const req = pendingRequests.find(r => r.from_user?.user_id === user.user_id);
                    if (req) handleAccept(req.request_id);
                  } : null}
                  onDecline={user.request_direction === 'received' ? () => {
                    const req = pendingRequests.find(r => r.from_user?.user_id === user.user_id);
                    if (req) handleDecline(req.request_id);
                  } : null}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  
  // Tab: ISLAND - ADE Island world
  const renderIslandTab = () => {
    // Get current user ID from localStorage
    const token = localStorage.getItem('token');
    let currentUserId = null;
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentUserId = payload.user_id;
      } catch (e) {}
    }
    
    return (
      <div className="flex flex-col gap-3" style={{ height: "calc(100dvh - 232px)" }}>
        {/* ADE Island Header — design cinematico */}
        <div className="relative shrink-0 overflow-hidden rounded-3xl border border-white/10"
          style={{ background: "linear-gradient(180deg, rgba(10,20,32,0.9), rgba(4,12,20,0.96))" }}>
          <div className="relative h-28 shrink-0">
            <img src="/images/ade-island.png" alt="" className="w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(4,12,20,0.15) 0%, rgba(4,12,20,0.85) 70%, rgba(4,12,20,0.98) 100%)" }} />
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
              <h2 className="text-[22px] font-black tracking-[0.14em] uppercase"
                style={{ background: "linear-gradient(100deg,#fff,#7FE7FF 55%,#FFD166)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                {t("adeWorld")}
              </h2>
              {myIslandName && (
                <p className="text-[11px] text-white/55 mt-0.5">
                  La tua isola <span className="font-bold text-cyan-200">{myIslandName}</span>
                </p>
              )}
            </div>
          </div>

          <div className="p-3 flex gap-2.5">
            <button
              onClick={() => setShowIslandSearch(true)}
              className="flex-1 py-3 rounded-2xl font-black text-[11.5px] uppercase tracking-[0.14em] text-cyan-100 flex items-center justify-center gap-2 transition-transform active:scale-[0.97]"
              style={{ background: "rgba(127,231,255,0.1)", border: "1px solid rgba(127,231,255,0.3)", boxShadow: "0 10px 28px rgba(20,140,190,0.16)" }}
              data-testid="search-islands-btn"
            >
              <Search className="w-4 h-4" />
              Cerca un'isola
            </button>
            <button
              onClick={() => setShowIslandShop(true)}
              className="flex-1 py-3 rounded-2xl font-black text-[11.5px] uppercase tracking-[0.14em] flex items-center justify-center gap-2 transition-transform active:scale-[0.97]"
              style={{ background: "linear-gradient(100deg,#FFD166,#FF9E3D)", color: "#0a0710", boxShadow: "0 10px 28px rgba(255,158,61,0.24)" }}
              data-testid="island-shop-btn"
            >
              <ShoppingBag className="w-4 h-4" />
              Shop
            </button>
          </div>
        </div>

        {/* World Map — riempie lo spazio fino in fondo */}
        <div id="world-map-section" className="flex-1 min-h-[160px]">
          <AdeWorldMap 
            fill
            currentUserId={currentUserId}
            flyToIsland={selectedIsland && !showTravelAnimation ? selectedIsland : null}
            onVisitIsland={(island) => {
              setSelectedIsland(island);
              setShowTravelAnimation(true);
            }}
          />
        </div>
        
      </div>
    );
  };
  
  // Tab: NEARBY with map + cancel button for pending requests
  const renderNearby = () => {
    // Function to disable GPS and go to ADE Island
    const disableGps = async () => {
      try {
        // Send ADE Island coordinates to backend
        await api.post('/location', {
          latitude: 35.0,
          longitude: -30.0
        });
        setGpsEnabled(false);
        toast.info('GPS disattivato. Benvenuto su ADE Island');
        fetchData(true);
      } catch (error) {
        toast.error('Errore nella disattivazione GPS');
      }
    };
    
    return (
    <div className="space-y-4">
      {/* GPS Status Indicator - More informative */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
        gpsEnabled 
          ? 'bg-green-50 text-green-700 border border-green-200' 
          : 'bg-amber-50 text-amber-700 border border-amber-200'
      }`}>
        <div className={`w-2.5 h-2.5 rounded-full ${gpsEnabled ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
        <div className="flex-1">
          <span className="font-medium">
            {gpsEnabled ? (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2.4} /> GPS attivo</span>
            ) : (
              <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" strokeWidth={2.4} /> {t('gpsNotActive')}</span>
            )}
          </span>
          {gpsEnabled && myLocation && (
            <p className="text-xs opacity-70">
              Lat: {myLocation.latitude.toFixed(5)}, Lon: {myLocation.longitude.toFixed(5)}
            </p>
          )}
          {!gpsEnabled && (
            <p className="text-xs opacity-70">
              {t('gpsActivateMsg')}
            </p>
          )}
        </div>
        
        {/* Attiva GPS button */}
        {!gpsEnabled && (
          <button 
            onClick={() => {
              if ('geolocation' in navigator) {
                toast.info('Richiesta permesso GPS...');
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    updateLocationToBackend({ 
                      latitude: pos.coords.latitude, 
                      longitude: pos.coords.longitude 
                    });
                    toast.success('GPS attivato!');
                    fetchData(true);
                  },
                  (err) => {
                    console.error('GPS Error:', err);
                    if (err.code === err.PERMISSION_DENIED) {
                      toast.error('Permesso negato. Vai in Impostazioni > Privacy > Posizione.');
                    } else if (err.code === err.POSITION_UNAVAILABLE) {
                      toast.error('GPS non disponibile. Attiva la posizione sul dispositivo.');
                    } else if (err.code === err.TIMEOUT) {
                      toast.error('Timeout GPS. Riprova all\'aperto.');
                    } else {
                      toast.error('Errore GPS. Riprova.');
                    }
                  },
                  { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
                );
              } else {
                toast.error('GPS non supportato');
              }
            }}
            className="px-3 py-1 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition-colors"
          >
            {t('activateGps')}
          </button>
        )}
        
        {/* Buttons when GPS is enabled */}
        {gpsEnabled && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    updateLocationToBackend({ 
                      latitude: pos.coords.latitude, 
                      longitude: pos.coords.longitude 
                    });
                    toast.success('Posizione aggiornata!');
                    fetchData(true);
                  },
                  () => toast.error('Errore aggiornamento'),
                  { enableHighAccuracy: true }
                );
              }}
              className="px-2 py-1 text-green-600 text-xs hover:underline"
            >
              Aggiorna
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={disableGps}
              className="px-2 py-1 text-red-500 text-xs hover:underline"
            >
              Disattiva
            </button>
          </div>
        )}
      </div>
      
      {/* Mini Map */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100">
        <div 
          ref={mapRef} 
          className="w-full h-48 bg-gradient-to-br from-orange-50 to-pink-50"
          style={{ zIndex: 1 }}
        />
        {!mapLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
            <div className="flex flex-col items-center gap-2">
              <AdeLoadingSpinner size="md" />
              <span className="text-sm text-gray-500">{t("loadingMap")}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Nearby Users List */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 px-1">
          {nearbyUsers.length} {nearbyUsers.length === 1 ? t('personNearby') : t('peopleNearby')}
        </h3>
        
        {nearbyUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gradient-to-br from-gray-50 to-orange-50/30 rounded-2xl">
            <RadarIcon active={false} className="w-16 h-16 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">{t('noOneNearby')}</p>
          </div>
        ) : (
          nearbyUsers.map((user) => {
            const isConnected = connections.some(c => c.user?.user_id === user.user_id);
            const pendingRequestId = getPendingRequestId(user.user_id);
            
            return (
              <div 
                key={user.user_id}
                className="flex items-center gap-3 p-4 ade-card-accent rounded-2xl shadow-sm"
              >
                <GoldenRingAvatar 
                  user={user}
                  size="md"
                  onClick={() => navigate(`/app/user/${user.user_id}`)}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 truncate">{user.name}</span>
                    {hasGoldenRing(user) && <BadgeCheck className="w-3.5 h-3.5 text-[#FFD700]" strokeWidth={2.4} />}
                    <ProBadge user={user} size="xs" showLabel={false} />
                  </div>
                  <p className="text-xs text-gray-500 truncate">{user.bio || 'Nelle vicinanze'}</p>
                  {user.distance && (
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3 text-pink-500" />
                      <span className="text-xs text-pink-500 font-medium">{user.distance}m</span>
                    </div>
                  )}
                </div>
                
                {isConnected ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    onClick={() => navigate(`/app/chat/${user.user_id}`)}
                  >
                    <MessageCircle className="w-5 h-5" />
                  </Button>
                ) : pendingRequestId ? (
                  <div className="flex items-center gap-2">
                    <PendingConnectionBar />
                    <button
                      onClick={() => handleCancelRequest(pendingRequestId)}
                      className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 transition-colors"
                      title="Annulla richiesta"
                    >
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleConnect(user.user_id)}
                    className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white rounded-full px-5 text-xs font-semibold shadow-lg"
                  >
                    Collega
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
  };
  
  // Tab: CONNECTIONS - Clean design without ugly circles
  // Tab: UNIFIED CONNECTIONS — Richieste + Collegati in un unico flusso
  const renderUnifiedConnections = () => (
    <div className="space-y-4">
      {/* 1. Richieste ricevute — pending fino a rifiuto/annullamento */}
      {pendingRequests.length > 0 && (
        <div>
          <h3 className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3 px-1">{t('pendingRequests')}</h3>
          <div className="space-y-3">
            {pendingRequests.map((request) => {
              const isHighlighted = highlightedRequestId && request.request_id === highlightedRequestId;
              const isIsland = request.source === 'island_doorbell' || request.via === 'island_doorbell';
              return (
              <div
                key={request.request_id}
                ref={isHighlighted ? highlightedCardRef : null}
                className={`flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 via-pink-50 to-purple-50 rounded-2xl border shadow-sm transition-all ${
                  isHighlighted
                    ? 'border-orange-400 ring-2 ring-orange-300 shadow-orange-200/60 scale-[1.01]'
                    : 'border-pink-100'
                }`}
                data-testid={`pending-request-${request.request_id}`}
              >
                <GoldenRingAvatar 
                  user={request.from_user}
                  size="md"
                  onClick={() => navigate(`/app/user/${request.from_user?.user_id}`)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 truncate">{request.from_user?.name}</span>
                    {hasGoldenRing(request.from_user) && <BadgeCheck className="w-3.5 h-3.5 text-[#FFD700]" strokeWidth={2.4} />}
                    <ProBadge user={request.from_user} size="xs" showLabel={false} />
                  </div>
                  {isIsland ? (
                    <p className="text-xs text-cyan-700 font-medium flex items-center gap-1" data-testid={`island-badge-${request.request_id}`}>
                      <Palmtree className="w-3.5 h-3.5 inline align-[-2px]" strokeWidth={2.4} /> richiede il collegamento dalla tua ADE Island
                    </p>
                  ) : (
                    <p className="text-xs text-pink-600 font-medium">ti ha inviato una richiesta di collegamento</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDecline(request.request_id)}
                    className="w-11 h-11 rounded-full bg-white/80 flex items-center justify-center hover:bg-red-50 transition-colors shadow-sm border border-gray-100"
                    data-testid={`decline-${request.request_id}`}
                  >
                    <X className="w-5 h-5 text-gray-400 hover:text-red-500" />
                  </button>
                  <button
                    onClick={() => handleAccept(request.request_id)}
                    className="w-11 h-11 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg shadow-green-200/50"
                    data-testid={`accept-${request.request_id}`}
                  >
                    <Check className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Richieste inviate — pending fino a risposta */}
      {sentRequests.length > 0 && (
        <div>
          <h3 className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3 px-1">{t('sentRequestsTitle')}</h3>
          <div className="space-y-3">
            {sentRequests.map((request) => (
              <div 
                key={request.request_id}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-orange-50/50 rounded-2xl border border-gray-100"
              >
                <GoldenRingAvatar user={request.to_user} size="md" />
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-gray-900 block truncate">{request.to_user?.name}</span>
                  {request.is_island_request && (
                    <span className="text-[10px] text-cyan-500 font-semibold inline-flex items-center gap-1">
                      <Palmtree className="w-3 h-3" strokeWidth={2.4} /> ADE Island
                    </span>
                  )}
                  <div className="mt-1.5"><ConnectionStatusBar status="pending" /></div>
                </div>
                <button
                  onClick={() => handleCancelRequest(request.request_id)}
                  className="p-2.5 rounded-full bg-red-50 hover:bg-red-100 transition-colors"
                  title="Annulla richiesta"
                  data-testid={`cancel-${request.request_id}`}
                >
                  <X className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Richieste già gestite (accettate/rifiutate): svaniscono in 24h */}
      {(() => {
        const handled = (requestHistory || [])
          .filter((r) => r.status === "accepted" || r.status === "declined")
          .map((r) => {
            const ts = new Date(r.resolved_at || r.created_at).getTime();
            const hours = (nowTs - ts) / 3600000;
            return { ...r, hours, life: Math.max(0, 1 - hours / 24) };
          })
          .filter((r) => r.life > 0);
        if (handled.length === 0) return null;
        return (
          <div>
            <div className="space-y-3">
              {handled.map((request) => {
                const u = request.from_user || request.to_user;
                const accepted = request.status === "accepted";
                return (
                  <div
                    key={request.request_id}
                    className={`flex items-center gap-3 p-4 rounded-2xl border shadow-sm transition-opacity duration-700 ${
                      accepted
                        ? "bg-gradient-to-r from-green-50 to-emerald-50/50 border-green-200"
                        : "bg-gradient-to-r from-gray-50 to-gray-100/60 border-gray-200"
                    }`}
                    style={{
                      animation: "none",
                      opacity: 0.08 + request.life * 0.92,
                      filter: `grayscale(${((1 - request.life) * 100).toFixed(0)}%)`,
                      maskImage: `linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,${request.life.toFixed(2)}) 100%)`,
                      WebkitMaskImage: `linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,${request.life.toFixed(2)}) 100%)`,
                    }}
                    data-testid={`handled-request-${request.request_id}`}
                    data-life={request.life.toFixed(2)}
                  >
                    <GoldenRingAvatar user={u} size="md" />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-gray-900 block truncate">{u?.name}</span>
                      <div className="mt-1.5"><ConnectionStatusBar status={accepted ? "accepted" : "declined"} /></div>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {accepted ? "Collegamento accettato" : "Richiesta rifiutata"}
                      </p>
                    </div>
                    {accepted && (
                      <button
                        onClick={() => navigate(`/app/chat/${u?.user_id}`)}
                        className="p-2.5 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
                        title="Invia messaggio"
                        data-testid={`chat-${request.request_id}`}
                      >
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* 4. Tutte le connessioni attive */}
      {connections.length > 0 && (
        <div>
          <h3 className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3 px-1">Collegati ({connections.length})</h3>
          <div className="space-y-3">
            {connections.map((connection) => (
              <div 
                key={connection.connection_id}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-white via-green-50/30 to-emerald-50/30 rounded-2xl shadow-sm border border-green-100/50 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/app/chat/${connection.user?.user_id}`)}
                data-testid={`connection-${connection.connection_id}`}
              >
                <GoldenRingAvatar user={connection.user} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 truncate">
                      {connection.from_island && connection.user?.island_name
                        ? `${connection.user.island_name} · ${connection.user?.name}`
                        : connection.user?.name}
                    </span>
                    {hasGoldenRing(connection.user) && <BadgeCheck className="w-3.5 h-3.5 text-[#FFD700]" strokeWidth={2.4} />}
                    <ProBadge user={connection.user} size="xs" showLabel={false} />
                  </div>
                </div>
                <ConnectedIndicator />
                <ChevronRight className="w-5 h-5 text-gray-300 ml-1" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {pendingRequests.length === 0 && sentRequests.length === 0 && connections.length === 0 && requestHistory.length === 0 && (
        <div className="text-center py-12 text-gray-400 bg-gradient-to-br from-gray-50 to-pink-50/30 rounded-2xl">
          <ChainLinkIcon active={false} className="w-16 h-16 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">{t("noConnectionYet")}</p>
          <p className="text-xs text-gray-400 mt-1">{t("requestsAppearHere")}</p>
        </div>
      )}
    </div>
  );
  

  const tabs = [
    { id: 'nearby', icon: RadarIcon, tooltip: 'Vicini', count: nearbyUsers.length },
    { id: 'island', icon: IslandIcon, tooltip: 'Isola', count: 0 },
    { id: 'connections', icon: ChainLinkIcon, tooltip: 'Connessioni', count: pendingRequests.length + connections.length },
    { id: 'explore', icon: ({ active }) => <Search className={`w-6 h-6 ${active ? 'text-amber-500' : 'text-gray-400'}`} />, tooltip: 'Esplora', count: 0 },
  ];
  
  return (
    <div className="page-content bg-gradient-to-br from-white via-orange-50/20 to-pink-50/20" data-testid="here-page">
      {/* Header */}
      <div className="sticky top-0 safe-top bg-white/90 backdrop-blur-xl z-[1000] border-b border-gray-100 shadow-sm">
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            {/* ADE Logo Refresh Button - LEFT side like Home page */}
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="here-radar relative focus:outline-none active:scale-95 transition-transform"
              title="Aggiorna"
              data-testid="ade-refresh-btn"
            >
              <img
                src="/images/ade-logo-extracted.png"
                alt="Aggiorna"
                className={`object-contain drop-shadow-lg transition-transform ${
                  isRefreshing ? 'animate-spin-slow' : 'hover:scale-105'
                }`}
                style={{ width: '56px', height: '56px' }}
              />
            </button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                {t('here')}
              </h1>
              <span className="text-[10px] text-gray-400 tracking-widest">{t('discoverNearby')}</span>
            </div>
          </div>
        </div>
        
        {/* Icon Tabs */}
        <div className="flex justify-center px-4 gap-3 pb-3">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'nearby') setMapLoaded(false);
                }}
                className={`relative flex flex-col items-center justify-center w-24 h-16 rounded-2xl transition-all duration-300 ${
                  isActive
                    ? 'ade-glow-card scale-105'
                    : 'ade-card hover:brightness-110'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <IconComponent active={isActive} />
                {tab.count > 0 && (
                  <span className={`absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] flex items-center justify-center rounded-full text-xs font-bold px-1.5 ${
                    isActive 
                      ? 'bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 text-white shadow-lg' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {tab.count > 99 ? '99+' : tab.count}
                  </span>
                )}
                {isActive && (
                  <div className="absolute -bottom-1 w-10 h-1.5 rounded-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <AdeLoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            {activeTab === 'nearby' && renderNearby()}
            {activeTab === 'island' && renderIslandTab()}
            {activeTab === 'connections' && renderUnifiedConnections()}
            {activeTab === 'explore' && <ExploreInline />}
          </>
        )}
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes scan-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-scan-bar {
          animation: scan-bar 1.5s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        .my-location-marker, .nearby-user-marker {
          background: transparent;
          border: none;
        }
      `}</style>
      
      {/* Island Search Modal */}
      {showIslandSearch && (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md">
            <IslandSearch 
              onIslandSelect={async (island) => {
                setShowIslandSearch(false);
                // Fetch position if missing
                if (!island.position_x || !island.position_y) {
                  try {
                    const res = await api.get(`/islands/${island.user_id}`);
                    island.position_x = res.data.position_x;
                    island.position_y = res.data.position_y;
                  } catch {}
                }
                setSelectedIsland(island);
                setShowTravelAnimation(true);
              }}
              onClose={() => setShowIslandSearch(false)}
            />
          </div>
        </div>
      )}
      
      {/* Island Shop */}
      {showIslandShop && (
        <IslandShop
          onClose={() => setShowIslandShop(false)}
          onPurchase={() => fetchData(false)}
        />
      )}
      
      {/* Travel Animation */}
      {showTravelAnimation && selectedIsland && (
        <IslandTravelAnimation
          destinationName={selectedIsland.island_name}
          isPremium={selectedIsland.is_premium}
          islandType={selectedIsland.island_type}
          onComplete={() => {
            setShowTravelAnimation(false);
          }}
        />
      )}
      
      {/* Island View - fullscreen map + bottom card */}
      {selectedIsland && !showTravelAnimation && (
        <div className="fixed inset-0 z-[9500] flex flex-col" style={{ background: "#04101f" }}>
          {/* Realistic island scene fills the WHOLE screen (no gaps) */}
          <div className="absolute inset-0">
            <IslandScene isPremium={!!selectedIsland.is_premium} islandType={selectedIsland.island_type} />
          </div>
          <div className="flex-1" />
          {/* Card at bottom, above the scene */}
          <div className="relative z-10">
            <IslandView
              island={selectedIsland}
              onBack={() => setSelectedIsland(null)}
              onConnectionChange={() => fetchData(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
